import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, ZoomIn, ZoomOut, AlertCircle } from "lucide-react";

interface MoleculeViewerProps {
  compoundName: string;
  chemicalName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface MoleculeData {
  imageUrl: string;
  molecularFormula?: string;
  molecularWeight?: string;
  iupacName?: string;
  pubchemUrl?: string;
}

export default function MoleculeViewer({ compoundName, chemicalName, isOpen, onClose }: MoleculeViewerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [moleculeData, setMoleculeData] = useState<MoleculeData | null>(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (isOpen && compoundName) {
      fetchMoleculeData();
    }
  }, [isOpen, compoundName]);

  const fetchMoleculeData = async () => {
    setLoading(true);
    setError(false);

    try {
      // Try to fetch from PubChem
      const searchName = encodeURIComponent(chemicalName || compoundName);
      const imageUrl = `https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/name/${searchName}/PNG`;
      
      // Test if image loads
      const img = new Image();
      img.onload = () => {
        setMoleculeData({
          imageUrl,
          pubchemUrl: `https://pubchem.ncbi.nlm.nih.gov/compound/${searchName}`,
          molecularFormula: "Available on PubChem",
          molecularWeight: "Available on PubChem",
          iupacName: chemicalName
        });
        setLoading(false);
      };
      img.onerror = () => {
        setError(true);
        setLoading(false);
      };
      img.src = imageUrl;
    } catch (err) {
      setError(true);
      setLoading(false);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.2, 0.5));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{compoundName}</DialogTitle>
          <DialogDescription>{chemicalName}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {loading && (
            <div className="space-y-4">
              <Skeleton className="h-96 w-full" />
              <div className="grid grid-cols-2 gap-4">
                <Skeleton className="h-20" />
                <Skeleton className="h-20" />
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="py-12 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-12 h-12 text-gray-400" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Structure Data Unavailable</h3>
              <p className="text-gray-600 mb-4">
                Molecular structure not found in PubChem database.
                <br />
                This may be a research compound or have an alternative name.
              </p>
              <Button
                variant="outline"
                onClick={() => window.open(`https://pubchem.ncbi.nlm.nih.gov/search/#query=${encodeURIComponent(compoundName)}`, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Search PubChem Manually
              </Button>
            </div>
          )}

          {moleculeData && !loading && !error && (
            <>
              {/* Molecular Structure Image */}
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">2D Molecular Structure</h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleZoomOut}
                      disabled={zoom <= 0.5}
                    >
                      <ZoomOut className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600 min-w-[60px] text-center">
                      {Math.round(zoom * 100)}%
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleZoomIn}
                      disabled={zoom >= 3}
                    >
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-center bg-gray-50 rounded-lg p-8 min-h-[300px]">
                  <img
                    src={moleculeData.imageUrl}
                    alt={`Molecular structure of ${compoundName}`}
                    style={{
                      transform: `scale(${zoom})`,
                      transition: 'transform 0.2s ease-in-out',
                      maxWidth: '100%',
                      height: 'auto'
                    }}
                    className="max-h-96"
                  />
                </div>
              </div>

              {/* Molecular Properties */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-[#F4F2EC] to-[#F4F2EC] rounded-lg p-4 border border-[#E4E1D8]">
                  <h4 className="text-sm font-medium text-gray-700 mb-1">IUPAC Name</h4>
                  <p className="text-sm text-gray-900">{moleculeData.iupacName || "View on PubChem"}</p>
                </div>
                <div className="bg-gradient-to-br from-[#F4F2EC] to-[#F4F2EC] rounded-lg p-4 border border-[#E4E1D8]">
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Molecular Formula</h4>
                  <p className="text-sm text-gray-900 font-mono">{moleculeData.molecularFormula}</p>
                </div>
              </div>

              {/* PubChem Link */}
              <div className="flex justify-center pt-4">
                <Button
                  onClick={() => window.open(moleculeData.pubchemUrl, '_blank')}
                  className="gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Full Details on PubChem
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
